import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const ProfileSettings: React.FC = () => {
  const { user, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    avatar: user?.avatar || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [saving, setSaving] = useState(false);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // In real app, this would call API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update local storage
      const updatedUser = { ...user!, ...profileData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setIsEditing(false);
      alert('Profile updated successfully!');
    } catch (error) {
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    
    try {
      // In real app, this would call API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordForm(false);
      alert('Password changed successfully!');
    } catch (error) {
      alert('Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoutAllDevices = async () => {
    if (window.confirm('This will log you out from all devices. Continue?')) {
      try {
        // In real app, this would invalidate all tokens
        await new Promise(resolve => setTimeout(resolve, 500));
        logout();
      } catch (error) {
        alert('Failed to logout from all devices');
      }
    }
  };

  return (
    <div className="profile-settings">
      <h3 className="profile-title">Profile Settings</h3>
      
      {/* Profile Information */}
      <div className="settings-section">
        <div className="section-header">
          <h4 className="settings-section-title">Personal Information</h4>
          <button 
            className="edit-button"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
        </div>

        {isEditing ? (
          <form onSubmit={handleProfileUpdate} className="profile-form">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                className="form-input"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                className="form-input"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Avatar URL (Optional)</label>
              <input
                type="url"
                value={profileData.avatar}
                onChange={(e) => setProfileData({...profileData, avatar: e.target.value})}
                className="form-input"
                placeholder="https://example.com/avatar.jpg"
              />
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => setIsEditing(false)} className="cancel-button">
                Cancel
              </button>
              <button type="submit" className="submit-button" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <div className="profile-display">
            <div className="profile-item">
              <span className="profile-label">Name</span>
              <span className="profile-value">{user?.name}</span>
            </div>
            <div className="profile-item">
              <span className="profile-label">Email</span>
              <span className="profile-value">{user?.email}</span>
            </div>
            <div className="profile-item">
              <span className="profile-label">Role</span>
              <span className={`role-badge role-${user?.role.toLowerCase()}`}>
                {user?.role}
              </span>
            </div>
            <div className="profile-item">
              <span className="profile-label">Account ID</span>
              <span className="profile-value" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {user?.id}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Security Settings */}
      <div className="settings-section">
        <div className="section-header">
          <h4 className="settings-section-title">Security</h4>
          <button 
            className="edit-button"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
          >
            Change Password
          </button>
        </div>

        {showPasswordForm && (
          <form onSubmit={handlePasswordChange} className="password-form">
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                className="form-input"
                required
              />
            </div>
            
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                className="form-input"
                minLength={6}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                className="form-input"
                minLength={6}
                required
              />
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => setShowPasswordForm(false)} className="cancel-button">
                Cancel
              </button>
              <button type="submit" className="submit-button" disabled={saving}>
                {saving ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        )}

        <div className="security-actions">
          <div className="security-item">
            <div>
              <div className="security-label">Session Management</div>
              <div className="security-description">Log out from all devices and invalidate all sessions</div>
            </div>
            <button onClick={handleLogoutAllDevices} className="danger-button">
              Logout All Devices
            </button>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="settings-section">
        <h4 className="settings-section-title">Account Information</h4>
        <div className="settings-item">
          <span className="settings-label">Account created</span>
          <span className="settings-value">
            {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : 'N/A'}
          </span>
        </div>
        <div className="settings-item">
          <span className="settings-label">Last login</span>
          <span className="settings-value">
            {user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }) : 'Current session'}
          </span>
        </div>
        <div className="settings-item">
          <span className="settings-label">Account status</span>
          <span className="settings-value" style={{ color: user?.isActive ? '#10b981' : '#ef4444' }}>
            {user?.isActive ? '● Active' : '● Inactive'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
